import IcMenu from '@assets/details-camera-icons/IcMenu';
import { GeoZoneCoordinates, GeoZones } from '@EcamModel/model';
import { Fade, Skeleton, Stack } from '@mui/material';
import { FormValues, HeadingSection } from '@pages/cameras/details/components/AddGeoZones';
import { Mode } from '@pages/cameras/details/components/CameraInformation';
import color from '@theme/Colors';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import FramePolygonGeoOverview from '../../../components/FramePolygonGeoOverview';
import useAddGeoZones from '../hook/useAddGeoZones';
import { PhotoSnapShotByPTZ, ViewPreset } from '@EcamModel/model/CameraView';
import { search } from '@pages/cameras/list/helpers';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import StyledAutocomplete from '@components/styled-autocomplete';
import { ISendActionGet } from '@EcamModel/controllers/IPTZCamConfigureRequestHttpController';
import { cameraViewController } from '@controllers/index';
import { pushError } from '@components/toast';
import { Filter } from '@pages/cameras/list';
import { Image, Layer, Stage } from 'react-konva';
import Konva from 'konva';
import { PREVIEW_RATIO } from '@pages/cameras/hook/CameraDetailsProvider';
import { IGetPhotoSnapShotByPTZ } from '@EcamModel/controllers/ICameraViewHttpController';
import { base64ToHTMLImageElement } from '../../components/PopupConfigViewCameraPTZ';
import { DrawerContext } from '@components/drawer/useDrawer';
import { PreviewImageWithGeoZone } from '../../../solar/components/HistoriesPhoto';
import { CameraType } from '@EcamModel/controllers/ICameraHttpController';
import { GiCctvCamera } from 'react-icons/gi';
import { BaseTextField } from '@components/BaseTextField';
import { checkUnmappedPreset } from '../../helpers';

interface Props {
    methods: UseFormReturn<any>;
    onChangeView: (
        condition: boolean,
        presetName: string,
        photoSnapShotByPTZ: PhotoSnapShotByPTZ,
        gotoPointId: number
    ) => void;
}

const FormAddGeoZonesPtzOverviewPage: FC<Props> = (props) => {
    const { methods, onChangeView } = props;
    const { open } = useContext(DrawerContext);
    const {
        imageElementPTZ,
        prevGeoZonePointsOverviewPage,
        selectedCameraType,
        loadingListGzByView,
        filteredCamera,
        listCameraView,
    } = useAddGeoZones({});

    const [loadingPreset, setLoadingPreset] = useState(true);
    const [isLoadingImage, setIsLoadingImage] = useState(true);
    const [listViewPreset, setListViewPreset] = useState<ViewPreset[]>([]);
    const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerSize, setContainerSize] = useState({
        width: window.innerWidth,
        height: window.innerWidth * PREVIEW_RATIO,
    });
    const [filter, setFilter] = useState<Filter>({
        page: 1,
        perspectiveView: listViewPreset?.[0],
    });
    const [photoSnapShotByPTZ, setPhotoSnapShotByPTZ] = useState<PhotoSnapShotByPTZ>({} as PhotoSnapShotByPTZ);
    const [openIMGPreview, setOpenIMGPreview] = useState<boolean>(false);

    const stageRef = useRef<Konva.Stage>(null);

    const isTypeOverview = selectedCameraType === 0;
    const scaleSize = containerSize.width / (imageElement?.width ?? 1920);
    const scaleFullPreview = 1280 / (imageElement?.width ?? 1920);

    const setValue = methods.setValue as (name: string, value: any, options?: Record<string, boolean>) => void;

    const handleDeleteGeoZonePoints = () => {
        setValue('geoZonePoints', [] as GeoZoneCoordinates[], {
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true,
        });
    };

    const getListPreset = async (_filter: ISendActionGet) => {
        setLoadingPreset(true);
        await cameraViewController
            .getListPreset(_filter)
            .then((res) => {
                const filteredNamePresets = res.filter((preset) => {
                    const name = preset.PresetName?.toLowerCase() || '';
                    return !['overview', 'over view'].some((keyword) => name.includes(keyword));
                });

                const unmappedPreset: ViewPreset[] = listCameraView
                    ? filteredNamePresets.filter(
                          (preset) => !checkUnmappedPreset(preset.GotoPointId, preset.PresetName, listCameraView)
                      )
                    : filteredNamePresets;

                setListViewPreset(unmappedPreset);

                if (!filter.perspectiveView?.GotoPointId) {
                    setFilter((prev) => ({
                        ...prev,
                        perspectiveView: {
                            GotoPointId: unmappedPreset[0].GotoPointId,
                            PresetName: unmappedPreset[0].PresetName,
                        },
                    }));
                }
            })
            .catch((err) => {
                pushError(err.response?.data?.message || 'Not found list preset.');
            })
            .finally(() => {
                setLoadingPreset(false);
            });
    };

    const getPhotoSnapShotByView = async (_filter: IGetPhotoSnapShotByPTZ) => {
        setIsLoadingImage(true);
        await cameraViewController
            .getPhotoSnapShotByPTZ(_filter)
            .then((res) => {
                setPhotoSnapShotByPTZ(res);
            })

            .finally(() => {
                setIsLoadingImage(false);
            });
    };

    const listsViewPresetAutocomplete = useStyledAutocomplete({
        list: {
            options: listViewPreset,
            isFiltered: (option, searchText) => search(option.PresetName ?? '', searchText),
        },
        dependencyList: [listViewPreset],
        loading: loadingPreset,
    });

    const conditionDisabled = isLoadingImage || !imageElement || filter.perspectiveView?.GotoPointId === undefined;

    useEffect(() => {
        if (filteredCamera && Object.keys(filteredCamera).length > 0) {
            getListPreset(filteredCamera);
        }
    }, [filteredCamera]);

    useEffect(() => {
        if (photoSnapShotByPTZ) {
            const convertBase64ToImage = async () => {
                try {
                    const imgElement = await base64ToHTMLImageElement(photoSnapShotByPTZ.Base64PhotoSnapShot);
                    setImageElement(imgElement);
                } catch (error) {
                    console.error('Failed to convert base64 to image', error);
                }
            };
            convertBase64ToImage();
        }
    }, [photoSnapShotByPTZ]);

    useEffect(() => {
        const updateStageSize = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                const height = width * PREVIEW_RATIO;
                setContainerSize({ width, height });
            } else {
                setTimeout(updateStageSize, 300);
            }
        };

        if (open) {
            setTimeout(updateStageSize, 300);
        } else {
            updateStageSize();
        }
        window.addEventListener('resize', updateStageSize);

        return () => {
            window.removeEventListener('resize', updateStageSize);
        };
    }, [open]);

    useEffect(() => {
        if (filter.perspectiveView?.GotoPointId !== undefined) {
            getPhotoSnapShotByView({
                PTZ: {
                    GotoPointId: filter.perspectiveView?.GotoPointId,
                },
                PTZCommand: filteredCamera,
            });
        }
    }, [filter.perspectiveView]);

    useEffect(() => {
        onChangeView(
            conditionDisabled,
            filter?.perspectiveView?.PresetName ?? '',
            photoSnapShotByPTZ,
            filter?.perspectiveView?.GotoPointId ?? 0
        );
    }, [conditionDisabled, filter, photoSnapShotByPTZ, onChangeView]);

    useEffect(() => {
        if (filter.perspectiveView?.PresetName) {
            setValue('geoZoneName', filter.perspectiveView.PresetName, {
                shouldValidate: true,
                shouldTouch: true,
                shouldDirty: true,
            });
        }
    }, [filter.perspectiveView?.PresetName]);

    return (
        <Fade in={isTypeOverview} timeout={800}>
            <Stack direction={'row'} spacing={2}>
                <Stack width={'30%'}>
                    <HeadingSection
                        icon={<GiCctvCamera color="#85858A" fontSize={20} stroke={color.grey600} />}
                        heading="Select view"
                    />

                    <Stack flex={1} spacing={2} mt={2}>
                        <Stack>
                            <StyledAutocomplete
                                {...listsViewPresetAutocomplete}
                                getOptionLabel={(o) => o.PresetName ?? ''}
                                label="View"
                                isRequired
                                disabled={isLoadingImage}
                                value={filter.perspectiveView}
                                disabledAllOption
                                onChange={(o) => {
                                    setFilter((prev) => ({ ...prev, perspectiveView: o }));
                                }}
                            />
                        </Stack>

                        <Stack alignItems={'center'} width={'100%'}>
                            {!imageElement || isLoadingImage ? (
                                <Skeleton
                                    ref={containerRef}
                                    sx={{
                                        borderRadius: '4px',
                                        bgcolor: color.grey200,
                                        minHeight: containerSize.height,
                                        width: '100%',
                                    }}
                                    variant="rectangular"
                                />
                            ) : (
                                <div
                                    ref={containerRef}
                                    style={{
                                        width: '100%',
                                        height: containerSize.height,
                                        position: 'relative',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Stage
                                        ref={stageRef}
                                        width={containerSize.width}
                                        height={containerSize.height}
                                        onClick={() => setOpenIMGPreview(true)}
                                    >
                                        <Layer>
                                            <Image image={imageElement} scaleX={scaleSize} scaleY={scaleSize} />
                                        </Layer>
                                    </Stage>
                                </div>
                            )}
                        </Stack>
                    </Stack>

                    <PreviewImageWithGeoZone
                        open={openIMGPreview}
                        onClose={() => {
                            setOpenIMGPreview(false);
                        }}
                        imgSrc={imageElement!}
                        scaleX={scaleFullPreview}
                        scaleY={scaleFullPreview}
                        geoZones={[]}
                        cameraType={CameraType.PTZ}
                    />
                </Stack>

                <Stack width={'70%'}>
                    <HeadingSection icon={<IcMenu />} heading="Zone marking" />

                    {/* Add GeoZone Name field */}
                    <Controller
                        name="geoZoneName"
                        control={methods.control}
                        render={({ field, fieldState: { error } }) => (
                            <BaseTextField
                                {...field}
                                label="Name"
                                isVariantOutlined
                                placeholder={'Geozone name'}
                                maxLength={50}
                                error={!!error?.message}
                                helperText={error?.message}
                                sx={{ width: '100%', mt: 2 }}
                            />
                        )}
                    />

                    <Controller
                        name="geoZonePoints"
                        control={methods.control}
                        render={({ field, fieldState: { error } }) => (
                            <FramePolygonGeoOverview
                                {...field}
                                imageElement={loadingListGzByView ? undefined : imageElementPTZ!}
                                mode={Mode.create}
                                prevGeoZone={prevGeoZonePointsOverviewPage as GeoZoneCoordinates[][]}
                                selectedGeoZone={{} as GeoZones}
                                onChange={(points) => {
                                    setValue('geoZonePoints', points, {
                                        shouldValidate: true,
                                        shouldTouch: true,
                                        shouldDirty: true,
                                    });
                                }}
                                onDelete={handleDeleteGeoZonePoints}
                                style={{
                                    width: '100%',
                                }}
                            />
                        )}
                    />
                </Stack>
            </Stack>
        </Fade>
    );
};

export default FormAddGeoZonesPtzOverviewPage;
